/**
 * Sesión del empleado. Los tokens viven SOLO en memoria: un script ajeno no puede leerlos y al cerrar
 * la pestaña la sesión termina. Al recargar se vuelve a la página de Cognito, que reconoce al empleado
 * sin pedirle la contraseña mientras su sesión allí siga vigente. El token de identidad dura 15 minutos
 * y se renueva solo, un minuto antes de vencer, con una sola renovación a la vez.
 */
import { createContext, use, useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import type { ProveedorIdentidad, Tokens } from "../api/oauth";
import { empleadoDelToken, type Empleado } from "./jwt";

const MARGEN_MS = 60_000;

export interface Sesion {
  readonly empleado: Empleado | null;
  /** La sesión terminó sola (vencida o rechazada por la API): la pantalla de ingreso lo explica. */
  readonly terminada: boolean;
  readonly identidad: ProveedorIdentidad;
  readonly iniciar: (tokens: Tokens) => void;
  readonly salir: () => Promise<void>;
  /** Token de identidad vigente (renovado si hace falta) o null si no hay sesión. */
  readonly obtenerToken: () => Promise<string | null>;
  /** La API respondió 401: la sesión ya no sirve. */
  readonly perder: () => void;
}

const Contexto = createContext<Sesion | null>(null);

export function ProveedorSesion({
  identidad,
  ahora = Date.now,
  children,
}: {
  readonly identidad: ProveedorIdentidad;
  readonly ahora?: () => number;
  readonly children: ReactNode;
}) {
  const tokens = useRef<Tokens | null>(null);
  const renovando = useRef<Promise<string | null> | null>(null);
  const [empleado, setEmpleado] = useState<Empleado | null>(null);
  const [terminada, setTerminada] = useState(false);

  const iniciar = useCallback((t: Tokens) => {
    tokens.current = t;
    setEmpleado(empleadoDelToken(t.idToken));
    setTerminada(false);
  }, []);

  const perder = useCallback(() => {
    if (!tokens.current) return;
    tokens.current = null;
    setEmpleado(null);
    setTerminada(true);
  }, []);

  const obtenerToken = useCallback(async (): Promise<string | null> => {
    const t = tokens.current;
    if (!t) return null;
    if (t.venceEn - ahora() > MARGEN_MS) return t.idToken;
    if (!t.refreshToken) {
      perder();
      return null;
    }
    const renovacion = t.refreshToken;
    renovando.current ??= identidad
      .renovar(renovacion)
      .then((nuevos) => {
        tokens.current = nuevos;
        return nuevos.idToken;
      })
      .catch(() => {
        perder();
        return null;
      })
      .finally(() => {
        renovando.current = null;
      });
    return renovando.current;
  }, [ahora, identidad, perder]);

  const salir = useCallback(async () => {
    const renovacion = tokens.current?.refreshToken ?? null;
    tokens.current = null;
    setEmpleado(null);
    setTerminada(false);
    await identidad.salir(renovacion);
  }, [identidad]);

  const valor = useMemo<Sesion>(
    () => ({ empleado, terminada, identidad, iniciar, salir, obtenerToken, perder }),
    [empleado, terminada, identidad, iniciar, salir, obtenerToken, perder],
  );
  return <Contexto value={valor}>{children}</Contexto>;
}

export function useSesion(): Sesion {
  const s = use(Contexto);
  if (!s) throw new Error("useSesion requiere ProveedorSesion");
  return s;
}
